import { Button, Modal } from "@legal-500/legal500-design-system";
import { useState, useEffect, type ReactNode } from "react";
import styles from "./TabbedModal.module.css";

export type Tab = {
  id: string;
  label: string;
  content: ReactNode;
};

type TabbedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  tabs: Tab[];
  actions?: ReactNode;
  defaultTab?: string;
  size?: "small" | "medium" | "large";
};

export function TabbedModal({
  isOpen,
  onClose,
  title,
  tabs,
  actions,
  defaultTab,
  size = "large",
}: TabbedModalProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultTab || tabs[0]?.id || "");

  // Reset to default tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab || tabs[0]?.id || "");
    }
  }, [isOpen, defaultTab, tabs]);

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size={size} blurBackground>
      <div className={styles.headerSection}>
        <div className={styles.tabs} role="tablist">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              className={activeTab === tab.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {actions && <div className={styles.modalActions}>{actions}</div>}
      </div>

      <div
        className={styles.tabContent}
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTabContent}
      </div>
    </Modal>
  );
}
